import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Spinner } from 'react-bootstrap';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { YFormField } from '../../../ui/YFormField';
import { YSheet } from '../../../ui/sheet/YSheet';
import { boardService } from '../services/boardService';
import { notifyClubBoardRead } from '../hooks/useClubBoardUnread';
import { formatBoardApiError, plainTextToBodyHtml } from '../board/boardDisplayUtils';
import '../../../ui/dialog/yDialog.css';

/**
 * President posts a club announcement to the Board (not E2EE Messages).
 * Server pins for 3 days when tag announcement is applied.
 */
export function ClubAnnouncementDialog({
  show,
  onHide,
  clubId,
  clubName,
  memberCount,
  onPosted,
}) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) {
      setTitle('');
      setMessage('');
      setError('');
      setSubmitting(false);
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clubId) return;
    const trimmedTitle = title.trim();
    const bodyHtml = plainTextToBodyHtml(message);
    if (!trimmedTitle || !bodyHtml) {
      setError('Add a subject and message.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const thread = await boardService.createClubAnnouncement(clubId, {
        title: trimmedTitle,
        body_html: bodyHtml,
      });
      onPosted?.(thread);
      notifyClubBoardRead();
      onHide();
    } catch (err) {
      setError(formatBoardApiError(err, 'Failed to post announcement.'));
    } finally {
      setSubmitting(false);
    }
  };

  const formId = 'club-announcement-form';

  return (
    <YSheet
      show={show}
      onHide={onHide}
      kicker="POST"
      title="Post club announcement"
      testId="club-announcement-dialog"
      error={error || undefined}
      footer={
        <>
          <YButton type="button" variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </YButton>
          <YButton type="submit" form={formId} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" role="status" />
                Posting…
              </>
            ) : (
              'Post to Board'
            )}
          </YButton>
        </>
      }
    >
      <Form id={formId} onSubmit={handleSubmit}>
        {clubName ? (
          <p className="clubs-page__dialog-note">
            Pinned on the board for 3 days. All {memberCount ?? 'club'} member
            {memberCount === 1 ? '' : 's'} of &ldquo;{clubName}&rdquo; can read and reply.
          </p>
        ) : null}
        <YFormField
          label="Subject"
          htmlFor="club-announcement-title"
          labelClassName="y-label y-label--dialog"
          required
        >
          <YInput
            id="club-announcement-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Season opener details"
            required
            disabled={submitting}
          />
        </YFormField>
        <YFormField
          label="Message"
          htmlFor="club-announcement-message"
          labelClassName="y-label y-label--dialog"
          required
        >
          <YInput
            id="club-announcement-message"
            type="textarea"
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your announcement…"
            required
            disabled={submitting}
          />
        </YFormField>
      </Form>
    </YSheet>
  );
}

ClubAnnouncementDialog.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  clubId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clubName: PropTypes.string,
  memberCount: PropTypes.number,
  onPosted: PropTypes.func,
};

ClubAnnouncementDialog.defaultProps = {
  show: false,
  clubId: null,
  clubName: '',
  memberCount: null,
  onPosted: null,
};
